import { Controller } from "@hotwired/stimulus"

// Drives the Choose-a-Plan page: a monthly/annual interval toggle, click-to-select
// plan cards (unselected cards dim), and a single Proceed button that submits the
// chosen plan + interval + member seats to Stripe Checkout.
export default class extends Controller {
  static targets = [
    "card", "intervalButton", "proceed", "fit", "memberSeats",
    "planInput", "intervalInput", "memberSeatsInput"
  ]

  connect() {
    this.interval = "monthly"
    this.selectedPlanId = null
    this.render()
  }

  selectCard(event) {
    this.selectedPlanId = event.currentTarget.dataset.planId
    this.render()
  }

  setInterval(event) {
    this.interval = event.currentTarget.dataset.interval
    this.render()
  }

  // type=submit; only fires when a plan is selected (button is disabled otherwise),
  // but guard anyway. Writes the chosen values into the form's hidden fields and
  // lets the (data-turbo=false) form submit to #subscribe → Stripe Checkout.
  proceed(event) {
    if (!this.selectedPlanId) {
      event.preventDefault()
      return
    }
    this.planInputTarget.value = this.selectedPlanId
    this.intervalInputTarget.value = this.interval
    // The seat quantity belongs to the fit block of the selected plan; the
    // other blocks are hidden and their inputs do not count.
    const fit = this.selectedFit()
    const seats = fit && fit.querySelector("[data-plan-selector-target~='memberSeats']")
    if (this.hasMemberSeatsInputTarget) this.memberSeatsInputTarget.value = seats ? seats.value : "0"
  }

  // Fires on form submit (after proceed() has written the values and allowed the
  // submit). Show a loading state while the server creates the Stripe customer +
  // Checkout session and redirects. Disabling here is safe — the submit is
  // already in flight, so it won't be cancelled.
  loading() {
    this.proceedTarget.disabled = true
    this.proceedTarget.style.opacity = "1" // override disabled:opacity-40 while loading
    this.proceedTarget.innerHTML =
      '<span class="inline-flex items-center justify-center gap-2">' +
      '<span class="h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin"></span>' +
      "Redirecting to checkout…</span>"
  }

  render() {
    // Cards: highlight the selected one, dim the rest once a choice is made.
    this.cardTargets.forEach((card) => {
      const selected = card.dataset.planId === this.selectedPlanId
      card.classList.toggle("ring-2", selected)
      card.classList.toggle("ring-indigo-500", selected)
      card.classList.toggle("border-indigo-500", selected)
      card.classList.toggle("opacity-50", Boolean(this.selectedPlanId) && !selected)
    })

    // Interval buttons: active state.
    this.intervalButtonTargets.forEach((btn) => {
      const active = btn.dataset.interval === this.interval
      btn.classList.toggle("bg-indigo-600", active)
      btn.classList.toggle("text-white", active)
      btn.classList.toggle("text-gray-600", !active)
      btn.classList.toggle("dark:text-gray-300", !active)
    })

    // Show the price spans that match the current interval, hide the others.
    this.element.querySelectorAll("[data-interval-price]").forEach((el) => {
      el.classList.toggle("hidden", el.dataset.intervalPrice !== this.interval)
    })

    this.fitTargets.forEach((block) => {
      block.classList.toggle("hidden", block.dataset.planId !== this.selectedPlanId)
    })
    // Only the owner gets the checkout form; everyone else browses the plans.
    if (this.hasProceedTarget) this.proceedTarget.disabled = !this.selectedPlanId
  }

  selectedFit() {
    return this.fitTargets.find((block) => block.dataset.planId === this.selectedPlanId) || null
  }
}
